// @ts-strict-ignore
import {Component, HostBinding, inject, Input, OnDestroy, OnInit, Optional, Self, ViewChild} from "@angular/core";
import {MatFormFieldControl} from "@angular/material/form-field";
import {Subject} from "rxjs";
import {QuillEditorComponent, QuillModules} from "ngx-quill";
import {ControlValueAccessor, FormControl, NgControl, Validators} from "@angular/forms";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {ErrorStateMatcher} from "@angular/material/core";
import {map} from "rxjs/operators";
import {ImageClickHandler} from "../quill-image-popup-module/OnClickModule";
import {SimMaterialDialogService} from "@services";
import {DomSanitizer} from "@angular/platform-browser";
import {StyleSafeDomSanitizer} from "../rich-text-editor-view/style-safe-dom-sanitizer.service";

@UntilDestroy()
@Component({
	selector: "sim-rich-text-editor-control",
	templateUrl: "./rich-text-editor-control.component.html",
	styleUrls: ["./rich-text-editor-control.component.scss"],
	providers: [
		{
			provide: DomSanitizer,
			useClass: StyleSafeDomSanitizer,
		},
		{
			provide: MatFormFieldControl,
			useExisting: RichTextEditorControlComponent,
		},
	],
	standalone: false,
})
export class RichTextEditorControlComponent implements OnInit, OnDestroy, ControlValueAccessor, MatFormFieldControl<string> {
	private static nextId = 0;

	@ViewChild(QuillEditorComponent, {static: true})
	editor: QuillEditorComponent;

	readonly controlType = "text-editor-form-field-control";

	@HostBinding()
	readonly id = `${this.controlType}-${RichTextEditorControlComponent.nextId++}`;

	form: FormControl<string>;

	readonly stateChanges = new Subject<void>();

	disabled: boolean;

	modules: QuillModules;

	dialogService = inject(SimMaterialDialogService);

	@Input() enableAdvancedFormatting = false;

	constructor(
		@Optional() @Self() public ngControl: NgControl,
		private _errorMatcher: ErrorStateMatcher,
	) {
		if (this.ngControl != null) {
			this.ngControl.valueAccessor = this;
		}

		this.form = new FormControl("");

		this.modules = {
			toolbar: [["bold", "italic"], ["image"], ["clean"]],

			onclick: {
				image: this._imageClickHandler,
			},
		};
	}

	ngOnInit(): void {
		this.form.valueChanges
			.pipe(
				untilDestroyed(this),
				// deleting the last character in the editor results in a null value
				map((v) => v ?? ""),
			)
			.subscribe((v) => {
				this.onChange(v);
			});

		if (this.enableAdvancedFormatting) {
			this.modules.toolbar = [
				["bold", "italic", "underline"],
				[{list: "ordered"}, {list: "bullet"}],
				[{header: [1, 2, 3, false]}],
				[{color: []}, {background: []}],
				["image"],
				["clean"],
			];
		}
	}

	private _imageClickHandler: ImageClickHandler = (dataUrl: string) => {
		this.dialogService
			.openImageDialog({
				type: "url",
				mediaUrl: dataUrl,
				mediaName: "Image",
			})
			.then();
	};

	ngOnDestroy(): void {
		this.stateChanges.complete();
	}

	get value() {
		return this.form.value;
	}

	@Input()
	set value(value: string) {
		this.form.setValue(value);
		this.stateChanges.next();
	}

	get placeholder() {
		return this._placeholder;
	}

	@Input()
	set placeholder(placeholder: string) {
		this._placeholder = placeholder;
		this.stateChanges.next();
	}

	private _placeholder = "";

	get focused(): boolean {
		return this._focused;
	}

	set focused(value: boolean) {
		this._focused = value;
		this.onTouched();
		this.stateChanges.next();
	}

	private _focused = false;

	get empty(): boolean {
		return !this.value;
	}

	@HostBinding("class.focused")
	get shouldLabelFloat(): boolean {
		return this.focused || !this.empty;
	}

	get required() {
		return this.ngControl.control.hasValidator(Validators.required);
	}

	get errorState(): boolean {
		return this._errorMatcher.isErrorState(this.ngControl.control, null);
	}

	onContainerClick(): void {
		this.editor.quillEditor.focus();
	}

	onChange = (_: string) => {};

	registerOnChange(fn: (_: string) => void): void {
		this.onChange = fn;
	}

	onTouched = () => {};

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean) {
		this.disabled = isDisabled;
		this.disabled ? this.form.disable() : this.form.enable();
		this.stateChanges.next();
	}

	writeValue(value: string): void {
		this.value = value;
	}

	userAriaDescribedBy: string;

	setDescribedByIds(ids: string[]): void {
		this.userAriaDescribedBy = ids.join(" ");
	}

	handleOnFocus() {
		this.focused = true;
	}

	handleOnBlur() {
		this.focused = false;
	}
}
